'use client';

import type { FieldContent, WebhookFieldAction, WebhookResponseRow } from '@capture/shared';
import { DerivedFieldSelect, isDerivedTargetName } from './DerivedFieldSelect';
import './WebhookResponseTable.css';

export interface WebhookResponseTableProps {
  rows: WebhookResponseRow[];
  /** Full field definitions for this job type — threaded straight into
   *  `DerivedFieldSelect`, which filters out operator-keyed fields itself. */
  fields: FieldContent[];
  onChange: (rows: WebhookResponseRow[]) => void;
}

/** Response-mapping builder for the webhook DERIVATION behavior
 *  (transform-webhook T6). Each row is a `{ path, target, onMiss? }` triple —
 *  `path` is a dotted lookup into the JSON response (`getResponsePath` in
 *  webhook-transform.ts), `target` is a derived/non-keyed field
 *  (`DerivedFieldSelect` enforces the exclusion), and `onMiss` controls what
 *  happens to `target` when `path` doesn't resolve. */
export function WebhookResponseTable({ rows, fields, onChange }: WebhookResponseTableProps) {
  function handlePathChange(index: number, path: string) {
    onChange(rows.map((r, i) => (i === index ? { ...r, path } : r)));
  }

  function handleTargetChange(index: number, target: string) {
    onChange(rows.map((r, i) => (i === index ? { ...r, target } : r)));
  }

  function handleOnMissActionChange(index: number, action: WebhookFieldAction['action']) {
    onChange(
      rows.map((r, i) => {
        if (i !== index) return r;
        if (action === 'default') {
          return { ...r, onMiss: { action, defaultValue: r.onMiss?.defaultValue ?? '' } };
        }
        return { ...r, onMiss: { action } };
      }),
    );
  }

  function handleDefaultValueChange(index: number, defaultValue: string) {
    onChange(
      rows.map((r, i) => (i === index ? { ...r, onMiss: { action: 'default', defaultValue } } : r)),
    );
  }

  function handleAddRow() {
    onChange([...rows, { path: '', target: '' }]);
  }

  function handleRemoveRow(index: number) {
    onChange(rows.filter((_, i) => i !== index));
  }

  return (
    <div className="webhook-response-table" data-testid="webhook-response-table">
      <span className="webhook-response-table__title">Response mapping</span>
      <p className="webhook-response-table__hint">
        Dots nest into the JSON response — e.g. <code>data.region</code>.
      </p>

      <table className="webhook-response-table__grid">
        <thead>
          <tr>
            <th>Response path</th>
            <th aria-label="maps to" />
            <th>Target field</th>
            <th>On miss</th>
            <th>Default value</th>
            <th aria-label="Remove row" />
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => {
            const action: WebhookFieldAction['action'] = row.onMiss?.action ?? 'leave';
            // A stale target: set, but no longer a derived field (deleted,
            // renamed, or reclassified as operator-keyed). DerivedFieldSelect
            // preserves it as a fallback option so it isn't silently blanked;
            // this flag tells the author the row is broken and must be re-picked
            // before publish (mirrors MappingPanel's `row-unfulfilled` flag).
            const staleTarget = row.target !== '' && !isDerivedTargetName(fields, row.target);
            return (
              // eslint-disable-next-line react/no-array-index-key
              <tr key={i}>
                <td>
                  <input
                    value={row.path}
                    onChange={(e) => handlePathChange(i, e.target.value)}
                    placeholder="data.region"
                    aria-label={`Response path ${i + 1}`}
                    data-testid={`webhook-response-path-${i}`}
                  />
                </td>
                <td className="webhook-response-table__arrow">→</td>
                <td>
                  <DerivedFieldSelect
                    fields={fields}
                    value={row.target}
                    onChange={(name) => handleTargetChange(i, name)}
                    ariaLabel={`Target field ${i + 1}`}
                    data-testid={`webhook-response-target-${i}`}
                  />
                  {staleTarget && (
                    <span
                      className="webhook-response-table__stale-flag"
                      data-testid={`webhook-response-target-invalid-${i}`}
                      title="Target is not a derived field — pick a derived field before publishing"
                    >
                      ⚠ not a derived field
                    </span>
                  )}
                </td>
                <td>
                  <select
                    value={action}
                    onChange={(e) => handleOnMissActionChange(i, e.target.value as WebhookFieldAction['action'])}
                    aria-label={`On miss action ${i + 1}`}
                    data-testid={`webhook-response-onmiss-${i}`}
                  >
                    <option value="leave">Leave</option>
                    <option value="clear">Clear</option>
                    <option value="default">Default</option>
                  </select>
                </td>
                <td>
                  {action === 'default' && (
                    <input
                      value={row.onMiss?.defaultValue ?? ''}
                      onChange={(e) => handleDefaultValueChange(i, e.target.value)}
                      placeholder="Default value"
                      aria-label={`Default value ${i + 1}`}
                      data-testid={`webhook-response-default-${i}`}
                    />
                  )}
                </td>
                <td>
                  <button
                    type="button"
                    className="webhook-response-table__remove-btn"
                    onClick={() => handleRemoveRow(i)}
                    aria-label={`Remove response row ${i + 1}`}
                    data-testid={`webhook-response-remove-${i}`}
                  >
                    ×
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <button
        type="button"
        className="webhook-response-table__add-btn"
        onClick={handleAddRow}
        data-testid="webhook-response-add-row"
      >
        + Add row
      </button>
    </div>
  );
}
